// InventoryProfileService.tsx
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { InventoryProfileService } from '../../core/services';
import { PageSize } from '../../type';
import { GetPurchaseOrderStatusesResponse, NewPurchaseOrderResponse } from './interface';

export const useGetInventoryService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-inventory-data'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getPurchaseOrder({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          poNumber: initialParams.poNumber,
          status: initialParams.status,
          supplierId: initialParams.supplierId,
          storeId: initialParams.storeId,
          startDate: initialParams.startDate,
          endDate: initialParams.endDate,
          urgencyStatus: initialParams.urgencyStatus,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 4,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetPurchaseOrdersTypeService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-purchase-orders-type'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getPurchaseOrdersType({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          type: initialParams.type,
          poNumber: initialParams.poNumber,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 4,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetPurchaseInvoiceService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-purchase-documents-data'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getPurchaseInvoices({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          invoiceNumber: initialParams.invoiceNumber,
          referenceNo: initialParams.referenceNo,
          invoiceType: initialParams.invoiceType,
          supplierId: initialParams.supplierId,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetStockReceivedService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-stock-received-data'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getStockReceipt({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          stockNumber: initialParams.stockNumber,
          supplierId: initialParams.supplierId,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetStockReceivedApprovedService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-stock-received-approved-data'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getStockReceiptApproved({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          type: initialParams.type,
          invoiceNumber: initialParams.invoiceNumber,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 4,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetCreditNoteService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-credit-note-data'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getCreditNote({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          pCDNNumber: initialParams.pCDNNumber,
          supplierId: initialParams.supplierId,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGeUnstockService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-unstock-data'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getUnstock({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          unStockNumber: initialParams.unStockNumber,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetStockTransferService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-stock-transfer-data'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getStockTransfer({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          sourceStoreId: initialParams.sourceStoreId,
          destinationStoreId: initialParams.destinationStoreId,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetStockAdjustmentService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-stock-adjustment-data'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getStockAdjustment({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          storeId: initialParams.storeId,
          itemCode: initialParams.itemCode,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetPurchaseOrderStatusesService = (poId: number) => {
  const { data, isLoading, error, refetch } = useQuery<GetPurchaseOrderStatusesResponse, unknown>({
    queryKey: ['get-po-statuses-data'],
    queryFn: async () => {
      return InventoryProfileService.getPurchaseOrderStatuses(poId);
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetPurchasePaymentService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-purchase-payments-data'],
      queryFn: async ({ pageParam }) => {
        return InventoryProfileService.getPurchasePayments({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          paymentNo: initialParams.paymentNo,
          referenceNo: initialParams.referenceNo,
          supplierName: initialParams.supplierName,
          start: initialParams.start,
          end: initialParams.end,
          supplierId: initialParams.supplierId,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetPurchaseOrderByIdService = (id: string | undefined) => {
  const { data, isLoading, error, refetch } = useQuery<NewPurchaseOrderResponse, unknown>({
    queryKey: ['get-po-by-id-data'],
    queryFn: async () => {
      return InventoryProfileService.getPurchaseOrderById(id);
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
